import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LandingPage } from '@/types/campaign';
import { saveCampaignConfiguration } from '@/utils/saveConfiguration';
import type { Campaign } from '@/types/campaign';
import { useCampaignStore } from '@/store/campaignStore';
import { apiClient } from '@/services/api/client';
import { SESSION_KEYS } from '@/config/constants';
import { termsApi } from '@/services/api/terms';
import { toast } from '@/utils';
import { RefreshButton } from '@/components/ui/RefreshButton';

interface LandingPagesListProps {
  landingPages: LandingPage[];
  onLandingPageToggle?: (url: string, authorized: boolean) => void;
  selectedLandingPages?: Map<string, boolean>;
  campaign?: Campaign;
  selectedAdGroups?: Set<string>;
}

export function LandingPagesList({
  landingPages,
  onLandingPageToggle,
  selectedLandingPages,
  campaign,
  selectedAdGroups
}: LandingPagesListProps) {
  const queryClient = useQueryClient();
  const triggerRefreshOnNav = useCampaignStore((state) => state.triggerRefreshOnNav);
  const [processingUrl, setProcessingUrl] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<string | null>(null);
  const [showRefreshConfirm, setShowRefreshConfirm] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Reset terms checkbox whenever dialog opens for a different URL
  // This prevents stale checkbox state if user cancels and opens dialog for another page
  useEffect(() => {
    setTermsAccepted(false);
  }, [showConfirmDialog]);

  // Get access state from parent's state if available, otherwise use page.authorized
  const isAuthorized = (page: LandingPage): boolean => {
    if (selectedLandingPages) {
      return selectedLandingPages.get(page.url) ?? page.authorized ?? false;
    }
    return page.authorized ?? false;
  };

  const handleAllowAccess = async (page: LandingPage) => {
    setShowConfirmDialog(null);
    setProcessingUrl(page.url);

    const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
    if (!memberUuid) {
      throw new Error('Member UUID not found in session');
    }

    try {
      // Log terms acceptance for audit trail - this MUST succeed for compliance
      console.log('[LandingPagesList] Recording terms acceptance for landing page scan');
      try {
        await termsApi.acceptTermsForLandingPageScan();
        console.log('[LandingPagesList] ✅ Terms acceptance recorded');
      } catch (termsError) {
        // Block the scan if terms acceptance cannot be recorded on the server
        // This ensures there is no scan without a corresponding server-side consent audit trail
        console.error('[LandingPagesList] Failed to record terms acceptance. Blocking scan.', termsError);
        toast.error('We could not record your terms acceptance. Please try again.');
        setProcessingUrl(null);
        return;
      }

      // Persist the permission BEFORE triggering the scrape — the scrape endpoint
      // reads flouri_scraping_enabled from the saved app config to authorize the URL.
      if (campaign && selectedAdGroups && selectedLandingPages) {
        // Create updated permissions map (React state updates are async!)
        const updatedPermissions = new Map(selectedLandingPages);
        updatedPermissions.set(page.url, true);

        console.log('[LandingPagesList] Saving configuration to persist scraping permission...');
        try {
          await saveCampaignConfiguration(campaign, selectedAdGroups, updatedPermissions);
          console.log('[LandingPagesList] ✅ Configuration saved');
        } catch (saveError) {
          console.error('[LandingPagesList] Failed to save configuration. Blocking scan.', saveError);
          toast.error('We could not save your scan permission. Please try again.');
          return;
        }
      }

      // Trigger scanning using apiClient for proper interceptors
      console.log('[LandingPagesList] Starting landing page scan for:', page.url);
      const response = await apiClient.post('/landing-pages/scrape', {
        member_uuid: memberUuid,
        urls: [page.url],
        force_rescrape: false
      });

      // Defense in depth: skipped > 0 here means the saved permission didn't take effect.
      if (response.data.skipped_scrapes > 0) {
        toast.error('Could not start scan: URL not authorized for scanning');
        return;
      }

      console.log('[LandingPagesList] ✅ Scanning started:', response.data);

      // Update the toggle state AFTER scanning request succeeds
      onLandingPageToggle?.(page.url, true);

      if (campaign && selectedAdGroups && selectedLandingPages) {
        // Wait briefly to ensure database write propagates fully
        await new Promise(resolve => setTimeout(resolve, 200));

        // Invalidate ALL campaign queries since normalization affects all campaigns
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        queryClient.invalidateQueries({ queryKey: ['campaign'] }); // Invalidate all individual campaign queries
        console.log('[LandingPagesList] Cache invalidated, data will refresh');

        // Trigger config refresh on next navigation
        triggerRefreshOnNav();
        console.log('[LandingPagesList] Refresh flag set for next navigation');
      }

    } catch (error) {
      console.error('Error granting access:', error);
      // No need to revert since we update state after success
    } finally {
      setProcessingUrl(null);
    }
  };

  const handleRemoveAccess = async (page: LandingPage) => {
    setProcessingUrl(page.url);

    try {
      // Update the toggle state first (no API call needed to revoke)
      onLandingPageToggle?.(page.url, false);

      // Save configuration if campaign and selectedAdGroups are available
      if (campaign && selectedAdGroups && selectedLandingPages) {
        // Create updated permissions map (React state updates are async!)
        const updatedPermissions = new Map(selectedLandingPages);
        updatedPermissions.set(page.url, false);

        console.log('[LandingPagesList] Saving configuration after removing access...');
        await saveCampaignConfiguration(campaign, selectedAdGroups, updatedPermissions);
        console.log('[LandingPagesList] ✅ Configuration saved');

        // Wait briefly to ensure database write propagates fully
        await new Promise(resolve => setTimeout(resolve, 200));

        // Invalidate ALL campaign queries since normalization affects all campaigns
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        queryClient.invalidateQueries({ queryKey: ['campaign'] }); // Invalidate all individual campaign queries
        console.log('[LandingPagesList] Cache invalidated, data will refresh');

        // Trigger config refresh on next navigation
        triggerRefreshOnNav();
        console.log('[LandingPagesList] Refresh flag set for next navigation');
      }

    } catch (error) {
      console.error('Error removing access:', error);
      // Revert the toggle on error
      onLandingPageToggle?.(page.url, true);
    } finally {
      setProcessingUrl(null);
    }
  };

  const handleToggleClick = (page: LandingPage) => {
    if (isAuthorized(page)) {
      handleRemoveAccess(page);
    } else {
      setShowConfirmDialog(page.url);
    }
  };

  const handleRefresh = async (page: LandingPage) => {
    setShowRefreshConfirm(null);
    setProcessingUrl(page.url);

    const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
    if (!memberUuid) {
      toast.error('Member UUID not found in session');
      setProcessingUrl(null);
      return;
    }

    try {
      console.log('[LandingPagesList] Refreshing landing page content for:', page.url);
      const response = await apiClient.post('/landing-pages/scrape', {
        member_uuid: memberUuid,
        urls: [page.url],
        force_rescrape: true
      });

      console.log('[LandingPagesList] Refresh response:', response.data);

      // Check if the URL was actually scraped or skipped
      if (response.data.skipped_scrapes > 0) {
        toast.error('Could not refresh: URL not authorized for scanning');
      } else {
        console.log('[LandingPagesList] ✅ Refresh started for:', page.url);
        toast.success('Refresh started. This may take a few minutes for complex pages.');

        // Invalidate cache so UI updates with new scraping status
        queryClient.invalidateQueries({ queryKey: ['campaigns'] });
        queryClient.invalidateQueries({ queryKey: ['campaign'] });
      }
    } catch (error) {
      console.error('[LandingPagesList] Error refreshing landing page:', error);
      toast.error('Failed to refresh landing page');
    } finally {
      setProcessingUrl(null);
    }
  };

  if (!landingPages || landingPages.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No landing pages found for this campaign</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3 max-h-[400px] overflow-y-auto bg-white rounded border border-gray-200 p-2">
        {landingPages.map((page, index) => {
          const authorized = isAuthorized(page);
          const isProcessing = processingUrl === page.url;

          return (
            <div
              key={page.hash || index}
              className="p-3 bg-white border border-gray-200 rounded hover:bg-gray-50 transition-colors"
            >
              <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                <div className="flex-1 min-w-0">
                  <div className="mb-1">
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 hover:underline break-all"
                    >
                      {page.url}
                    </a>
                  </div>
                  {page.source_name && (
                    <div className="text-xs text-gray-500">
                      Found in: {page.source_name} ({page.source_type || 'ad'})
                    </div>
                  )}
                </div>
                {onLandingPageToggle && (
                  <div className="flex gap-2 flex-shrink-0">
                    {authorized && (
                      <RefreshButton
                        onClick={() => setShowRefreshConfirm(page.url)}
                        isRefreshing={isProcessing}
                        title="Updated your page? Press Refresh so we can re-analyze your content."
                      />
                    )}
                    <button
                      onClick={() => handleToggleClick(page)}
                      disabled={isProcessing}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                        isProcessing
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : authorized
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {isProcessing
                        ? 'Processing...'
                        : authorized
                        ? 'Remove Access'
                        : 'Allow Access'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Allow Flouri to scan your webpage?
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              Flouri will analyze the content of this landing page to provide personalized keyword recommendations.
            </p>
            <p className="text-sm text-blue-600 mb-4 break-all">
              {showConfirmDialog}
            </p>

            {/* Terms Agreement Checkbox */}
            <label className="flex items-start gap-3 mb-6 cursor-pointer">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-600">
                I agree to the{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  Privacy Policy
                </a>
              </span>
            </label>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDialog(null)}
                className="px-4 py-2 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const page = landingPages.find(p => p.url === showConfirmDialog);
                  if (page) handleAllowAccess(page);
                }}
                disabled={!termsAccepted}
                className={`px-4 py-2 rounded-md text-white font-medium transition-colors ${
                  termsAccepted
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-400 cursor-not-allowed'
                }`}
              >
                Allow Access
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Confirmation Dialog */}
      {showRefreshConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Recently updated this landing page?
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              We'll re-analyze this page and provide up-to-date recommendations.
            </p>
            <p className="text-sm text-blue-600 mb-4 break-all">
              {showRefreshConfirm}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRefreshConfirm(null)}
                className="px-4 py-2 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const page = landingPages.find(p => p.url === showRefreshConfirm);
                  if (page) handleRefresh(page);
                }}
                className="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700 font-medium transition-colors"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
